import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import Check from "@mui/icons-material/Check";
import { Box, IconButton, Stack, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { CellWithSecondaryText } from "components/DataGrid";
import { FacilitiesExportButton } from "components/FacilitiesExportButton";
import { Importer } from "components/Importer";
import { ImportDropdown } from "components/Importer/ImportDropdown";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { OneSchemaTemplateType } from "encamp-shared/src/oneSchema/types";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getChemicalsBreadcrumb, getChemicalsInventoryBreadcrumb } from "util/breadcrumb";

import { gql } from "../../../../generated-graphql";
import { InventoryFacilitiesQuery } from "../../../../generated-graphql/graphql";
import { useBreadcrumb } from "../../../../hooks/useBreadcrumbs";

type Row = InventoryFacilitiesQuery["chemicalEnabledFacilities"]["items"][0];

const INVENTORY_FACILITIES = gql(`
  query InventoryFacilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    chemicalEnabledFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        customerFacilityId
        collectionMode
        streetAddress1
        city
        state
        isSubjectToEmergencyPlanning
      }
      count
    }
  }
`);

export function FacilityInventoryTable() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { isStaff } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useBreadcrumb([
    {
      label: "Chemicals",
      to: tenantId ? getChemicalsBreadcrumb(tenantId) : undefined,
    },
    {
      label: "Inventory",
      to: tenantId ? getChemicalsInventoryBreadcrumb(tenantId) : undefined,
    },
  ]);

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "facility",
        headerName: "Facility",
        filterKeyType: "facility",
        flex: 0.5,
        renderCell: (params) => (
          <CellWithSecondaryText
            primaryValue={
              params.row.customerFacilityId
                ? `${params.row.name} (${params.row.customerFacilityId})`
                : params.row.name
            }
            secondaryValue={`${
              params.row.streetAddress1 ? params.row.streetAddress1 : ""
            }, ${params.row.city ? `${params.row.city}, ` : ""}${
              params.row.state ? `${params.row.state}` : ""
            }`}
          />
        ),
      },

      {
        field: "state",
        headerName: "State/Territory",
        flex: 0.25,
      },
      {
        field: "emergencyPlanning",
        headerName: "Emergency Planning?",
        flex: 0.4,
        type: "boolean",
        headerAlign: "center",
        renderCell: (params) => {
          const { isSubjectToEmergencyPlanning } = params.row;
          return (
            <Stack direction="row" justifyContent="center" sx={{ width: "100%" }}>
              {isSubjectToEmergencyPlanning ? <Check /> : "-"}
            </Stack>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        flex: 0.2,
        getActions: (params) => [
          <Tooltip title="View facility" key={1}>
            <IconButton
              size="small"
              href={`${location.pathname}/${params.row.id}`}
              aria-label="View facility"
            >
              <ArrowCircleRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>,
        ],
      },
    ],
    []
  );

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const renderImportExportButtons = useCallback(
    ({ search }: { search: string }) => {
      const buttons = [];
      if (isStaff) {
        buttons.push(
          <ImportDropdown
            key="import"
            importers={[
              {
                name: "Facilities",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.Facility}
                    refetchQueries={["InventoryFacilities"]}
                  />
                ),
              },
              {
                name: "Facility Extensions (state fields, etc)",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.FacilityStateField}
                    refetchQueries={["InventoryFacilities"]}
                  />
                ),
              },
              {
                name: "Facility Alternate IDs",
                importer: (
                  <Importer
                    templateType={OneSchemaTemplateType.FacilityAlternateId}
                    refetchQueries={["InventoryFacilities"]}
                  />
                ),
              },
            ]}
          />
        );
      }
      buttons.push(
        <FacilitiesExportButton
          key="export"
          tenantId={tenantId}
          filter={search ? { search } : undefined}
        />
      );
      return buttons;
    },
    [isStaff, tenantId]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="chemicals-inventory-facilities"
        isURLDriven
        columns={columns}
        dataQuery={INVENTORY_FACILITIES}
        defaultSearch={`tenantId:${tenantId} chemicalFeatureFlagOverride:!DISABLED`}
        getItems={(data) => data.chemicalEnabledFacilities.items}
        getCount={(data) => data.chemicalEnabledFacilities.count}
        columnVisibilityModel={{
          alternateId: !isSmallScreen && !isMobile,
          streetAddress1: !isSmallScreen && !isMobile,
          city: !isSmallScreen && !isMobile,
          ...(isMobile && {
            emergencyPlanning: false,
          }),
        }}
        initialSortModel={[{ field: "facility", sort: "asc" }]}
        noDataMessage="No facilities have been added to this organization yet."
        onRowClick={({ row }) => navigate(row.id)}
        getRowLink={(row) => `./${row.id}`}
        commandButtons={renderImportExportButtons}
      />
    </Box>
  );
}
